// Onda 5 — Modal de emitir NF. Atualiza a parcela existente: status=faturada + NF.
// NÃO cria parcela nova (substitui o antigo "fin_faturar_entregavel").
import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { hojeIso } from '../../utils/dataFmt';
import { getToken } from '../../utils/auth';
import { fmt, fmtData } from './utils';
import type { Parcela } from './types';

export function ModalEmitirNf({ parcela, onClose, onSucesso }: {
  parcela: Parcela; onClose: () => void; onSucesso: () => void;
}) {
  const hoje = hojeIso();
  const [form, setForm] = useState({
    nf_numero: '',
    nf_emitida_em: hoje,
    valor_previsto: String(parcela.valor_previsto || ''),
    data_vencimento: parcela.data_vencimento || hoje,
    meio_pagamento: parcela.meio_pagamento || 'boleto',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function salvar() {
    setErro('');
    if (!form.nf_numero.trim()) { setErro('Número da NF é obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_emitir_nf_parcela',
        parcela_id: parcela.id,
        nf_numero: form.nf_numero.trim(),
        nf_emitida_em: form.nf_emitida_em || null,
        valor_previsto: form.valor_previsto ? parseFloat(form.valor_previsto) : undefined,
        data_vencimento: form.data_vencimento || undefined,
        meio_pagamento: form.meio_pagamento || null,
      }, { headers: { Authorization: token } });
      onSucesso();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 4,
    border: '0.5px solid #ddd', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, color: '#555', display: 'block', marginBottom: 3, fontWeight: 600,
  };

  // FIN-UI (2026-07-14): portalizado pro body (fixed dentro das Tabs do hub caía pra baixo).
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', padding: 24, borderRadius: 8, width: '92%', maxWidth: 540 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#020D7F' }}>🧾 Emitir NF</h3>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
          {parcela.contrato_numero} · {parcela.cliente_nome || 'sem cliente'}
        </div>
        <div style={{ background: '#FAFBFF', padding: 12, borderRadius: 6,
          border: '0.5px solid #E4E7F1', fontSize: 12, marginBottom: 14 }}>
          <strong>{parcela.parcela_titulo || parcela.descricao}</strong>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            Valor previsto: {fmt(parcela.valor_previsto)}
            {' · '}Vencimento: {fmtData(parcela.data_vencimento)}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Número da NF *</label>
            <input value={form.nf_numero}
              onChange={e => setForm({ ...form, nf_numero: e.target.value })}
              placeholder="Ex.: 12345" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Data emissão NF</label>
            <input type="date" value={form.nf_emitida_em}
              onChange={e => setForm({ ...form, nf_emitida_em: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={lbl}>Valor (ajustável)</label>
            <input type="number" step="0.01" value={form.valor_previsto}
              onChange={e => setForm({ ...form, valor_previsto: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={lbl}>Vencimento (ajustável)</label>
            <input type="date" value={form.data_vencimento}
              onChange={e => setForm({ ...form, data_vencimento: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={lbl}>Meio de pagamento</label>
            <select value={form.meio_pagamento}
              onChange={e => setForm({ ...form, meio_pagamento: e.target.value })} style={inp}>
              <option value="">—</option>
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
              <option value="ted">TED</option>
              <option value="deposito">Depósito</option>
              <option value="cheque">Cheque</option>
              <option value="outro">Outro</option>
            </select>
          </div>
        </div>
        {erro && (
          <div style={{ padding: 10, background: '#FFF7F7', border: '0.5px solid #FBD9D9',
            borderRadius: 5, color: '#B91626', fontSize: 12, marginBottom: 12 }}>
            ⚠ {erro}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '8px 16px', fontSize: 12, border: '0.5px solid #ddd',
              background: '#fff', color: '#555', borderRadius: 5,
              cursor: salvando ? 'wait' : 'pointer' }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando || !form.nf_numero.trim()}
            style={{ padding: '8px 22px', fontSize: 12, fontWeight: 600,
              background: form.nf_numero.trim() ? '#0930D0' : '#bbb', color: '#fff',
              border: 'none', borderRadius: 5,
              cursor: salvando ? 'wait' : (form.nf_numero.trim() ? 'pointer' : 'not-allowed') }}>
            {salvando ? 'Emitindo...' : '🧾 Emitir NF'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
